import React, { useState, useMemo, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  DefaultButton,
  DefaultPrimaryButton,
  InputError,
  InputWithLabel,
} from "../../../components/micro";
import ConfirmationModel from "../../../components/common/ConfirmationModel";
import Breadcrumb from "../../../components/common/Breadcrumb";
import FileUploadChoose from "../../../components/common/FileUploadChoose";
import { toast } from "react-toastify";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { createTips, updateTips } from "../../../redux/nutrition-tips/slice";
import { useDispatch } from "react-redux";
import { uploadImage } from "../../../redux/uploadFile/slice";

const AddEditTip = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tipsData = location?.state?.nutrition;
  const isEditMode = location?.state?.mode;
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  // const [tipImages, setTipImages] = useState([]);

  // useEffect(() => {
  //   if (tipsData?.tipImage) {
  //     setTipImages([{ url: tipsData?.tipImage, id: "tipImages" }]);
  //   }
  // }, [tipsData]);

  const quillRef = useRef();

  const initialValues = useMemo(
    () => ({
      tipName: tipsData?.tipName || "",
      nutritionName: tipsData?.nutritionName || "",
      nutritionistCredentials: tipsData?.nutritionCredentials || "",
      details: tipsData?.details || "",
      duration: tipsData?.duration || "",
      instruction: tipsData?.instruction || "",
      tipImage: tipsData?.tipImage || "", // for preview
      tipImageKey: tipsData?.tipImageKey || "", // for API
      nutritionListImage: tipsData?.nutritionListImage || "", // URL for preview
      nutritionListImageKey: tipsData?.nutritionListImageKey || "", // Key for API payload
      tipsForRecovery: tipsData?.betterRecovery?.length
        ? tipsData.betterRecovery
        : [""],
    }),
    [tipsData]
  );

  const validationSchema = Yup.object().shape({
    tipName: Yup.string().trim().nullable().required("Please enter tip name"),
    nutritionName: Yup.string()
      .trim()
      .nullable()
      .required("Please enter nutrition name"),
    nutritionistCredentials: Yup.string()
      .trim()
      .nullable()
      .required("Please enter Nutritionist Credentials"),
    details: Yup.string().trim().nullable().required("Please enter details"),
    duration: Yup.number().required("Please enter duration"),
    instruction: Yup.string()
      .test("is-not-empty", "Please enter instruction", (value) => {
        if (!value) return false;
        const text = value.replace(/<[^>]+>/g, "").trim();
        return text.length > 0;
      })
      .required("Please enter instruction"),
    nutritionListImage: Yup.mixed().required("Nutritionist image is required"),
    nutritionListImageKey: Yup.string().required(
      "Nutritionist image key is required"
    ),
    tipImage: Yup.string().required("Please upload an image"),
    tipImageKey: Yup.string().required("Image key is required"),
  });
  const handleConfirm = () => {
    setIsLoading(true);

    const payload = {
      tipName: formValues?.tipName,
      nutritionName: formValues?.nutritionName,
      details: formValues?.details,
      duration: Number(formValues?.duration),
      instruction: formValues?.instruction,
      tipImage: formValues?.tipImageKey || "",
      nutritionListImage: formValues?.nutritionListImageKey || "",
      nutritionCredentials: formValues?.nutritionistCredentials || "",
      betterRecovery: formValues?.tipsForRecovery || [],
    };

    const action =
      isEditMode === "edit"
        ? updateTips({
            _id: tipsData._id,
            ...payload,
          })
        : createTips(payload);

    dispatch(action)
      .then((res) => {
        if (
          res.type === "updateTips/fulfilled" ||
          res.type === "createTips/fulfilled"
        ) {
          navigate("/nutrition-tips");
        }
      })
      .finally(() => {
        setIsLoading(false);
        setShowConfirmationModal(false);
      });
  };

  const handleEditClick = () => setIsEditing(true);
  const handleCancelClick = () => navigate("/nutrition-tips");

  const handleFileChange = async (event, setFieldValue, fieldType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const tempURL = URL.createObjectURL(file);

    // Set preview first
    if (fieldType === "tip") {
      setFieldValue("tipImage", tempURL);
    } else {
      setFieldValue("nutritionListImage", tempURL);
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const result = await dispatch(uploadImage(formData));

      if (uploadImage.fulfilled.match(result)) {
        const fileUrl = result.payload.data.url;
        const fileKey = result.payload.data.key;

        if (fieldType === "tip") {
          setFieldValue("tipImage", fileUrl);
          setFieldValue("tipImageKey", fileKey);
        } else {
          setFieldValue("nutritionListImage", fileUrl);
          setFieldValue("nutritionListImageKey", fileKey);
        }
      } else {
        toast.error("Upload failed");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const [isUploading, setIsUploading] = useState(false);

  const handleMediaUpload = async (type) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = type === "image" ? "image/*" : "video/*";
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      setIsUploading(true); // show loader
      try {
        const formData = new FormData();
        formData.append("file", file);

        const resultAction = await dispatch(uploadImage(formData));

        if (!uploadImage.fulfilled.match(resultAction)) {
          throw new Error("Upload failed");
        }

        const fileUrl = resultAction.payload?.data?.url;
        if (!fileUrl) throw new Error("File URL missing");

        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const range = quill.getSelection(true);
        if (!range) return;

        if (type === "image") {
          quill.insertEmbed(range.index, "image", fileUrl, "user");
        } else {
          quill.insertEmbed(range.index, "video", fileUrl, "user");
        }

        quill.setSelection(range.index + 1);
      } catch (err) {
        console.error("Upload failed:", err);
      } finally {
        setIsUploading(false); // hide loader
      }
    };
  };
  const Size = Quill.import("attributors/style/size");
  // allowed sizes
  Size.whitelist = ["12px", "14px", "16px", "18px", "24px"];
  Quill.register(Size, true);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, false] }],
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image", "video"],
          ["clean"],
          [{ size: ["12px", "14px", "16px", "18px", "24px"] }],
        ],
        handlers: {
          image: () => handleMediaUpload("image"),
          video: () => handleMediaUpload("video"),
        },
      },
    }),
    []
  );

  const formats = [
    "header",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
    "video",
  ];

  return (
    <>
      <div className="mb-5">
        <h6 className="font-Montserrat font-medium text-xl mb-3">
          {isEditMode === "edit" ? "Edit Tip" : "Create Tip"}
        </h6>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={isEditMode === "edit" ? "Edit Tip" : "Create Tip"}
          breadCrumbParent3={"Tip"}
          breadCrumbParent2={"Nutrition"}
          breadCrumbTitleKey={"/"}
        />
      </div>

      <div className="w-full bg-bgWhite shadow rounded-lg p-6">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setFormValues(values);
            setShowConfirmationModal(true);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputWithLabel
                  idFromName="tipName"
                  label="Tip Name*"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors.tipName && touched.tipName}
                  className={`${
                    errors?.tipName && touched?.tipName
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  } w-full `}
                  value={values.tipName}
                  errorsText={errors.tipName}
                  disabled={isEditMode === "edit" && !isEditing}
                />
                <InputWithLabel
                  idFromName="duration"
                  label="Duration*"
                  type="number"
                  onFocus={(e) =>
                    e.target.addEventListener(
                      "wheel",
                      function (e) {
                        e.preventDefault();
                      },
                      { passive: false }
                    )
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.duration}
                  errors={errors.duration && touched.duration}
                  className={`${
                    errors?.duration && touched?.duration
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  } w-full `}
                  errorsText={errors.duration}
                  disabled={isEditMode === "edit" && !isEditing}
                />
                <InputWithLabel
                  idFromName="details"
                  label="Details*"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.details}
                  errors={errors.details && touched.details}
                  className={`${
                    errors?.details && touched?.details
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  } w-full `}
                  errorsText={errors.details}
                  disabled={isEditMode === "edit" && !isEditing}
                />
                <InputWithLabel
                  idFromName="nutritionName"
                  label="Nutrition Name*"
                  value={values.nutritionName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors.nutritionName && touched.nutritionName}
                  className={`${
                    errors?.nutritionName && touched?.nutritionName
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  } w-full `}
                  errorsText={errors.nutritionName}
                  disabled={isEditMode === "edit" && !isEditing}
                />

                {/* NutritionistCredentials */}
                <InputWithLabel
                  idFromName="nutritionistCredentials"
                  label="Nutritionist Credentials*"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.nutritionistCredentials}
                  errors={
                    errors.nutritionistCredentials &&
                    touched.nutritionistCredentials
                  }
                  className={`${
                    errors?.nutritionistCredentials &&
                    touched?.nutritionistCredentials
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  } w-full `}
                  errorsText={errors.nutritionistCredentials}
                  disabled={isEditMode === "edit" && !isEditing}
                />
                <div></div>

                {/* Nutritionist Image Upload */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                    Nutritionist Image*
                  </label>
                  <FileUploadChoose
                    className={`border border-borderTheme/80 transition duration-300 ease-in-out ${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "border border-borderTheme/80"
                    } ${
                      errors.nutritionListImage && touched.nutritionListImage
                        ? "border-red"
                        : ""
                    }`}
                    disabled={isEditMode === "edit" && !isEditing}
                    accept="image/*"
                    filemsg="Upload JPG, PNG, JPEG file"
                    handleFileChange={(e) =>
                      handleFileChange(e, setFieldValue, "nutritionist")
                    }
                    uploadedFiles={
                      values.nutritionListImageKey
                        ? [{ url: values.nutritionListImage }]
                        : []
                    }
                    onDelete={() => {
                      setFieldValue("nutritionListImage", "");
                      setFieldValue("nutritionListImageKey", "");
                    }}
                  />
                  {errors.nutritionListImage && touched.nutritionListImage ? (
                    <InputError errorTitle={errors.nutritionListImage} />
                  ) : null}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                    Tip Image*
                  </label>
                  <FileUploadChoose
                    className={`border border-borderTheme/80 transition duration-300 ease-in-out ${
                      errors.tipImage && touched.tipImage ? "border-red" : ""
                    }`}
                    disabled={isEditMode === "edit" && !isEditing}
                    handleFileChange={(event) =>
                      handleFileChange(event, setFieldValue, "tip")
                    }
                    accept="image/*"
                    filemsg="Upload JPG, PNG, JPEG file"
                    uploadedFiles={
                      values.tipImageKey ? [{ url: values.tipImage }] : []
                    }
                    onDelete={() => {
                      setFieldValue("tipImage", "");
                      setFieldValue("tipImageKey", "");
                    }}
                  />
                  {errors.tipImage && touched.tipImage && (
                    <InputError errorTitle={errors.tipImage} />
                  )}
                </div>

                <FieldArray
                  name="tipsForRecovery"
                  render={(arrayHelpers) => (
                    <div className="space-y-3">
                      {(values.tipsForRecovery || []).length > 0
                        ? (values.tipsForRecovery || []).map((tag, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 "
                            >
                              {/* Tag Input */}
                              <div className="w-full">
                                <InputWithLabel
                                  name={`tipsForRecovery[${index}]`}
                                  label={`Tips For Better Recovery`}
                                  value={tag}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  className={`${
                                    errors?.tipsForRecovery?.[index] &&
                                    touched?.tipsForRecovery?.[index]
                                      ? `border border-red`
                                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                                  } w-full `}
                                  errorsText={errors?.tipsForRecovery?.[index]}
                                  disabled={isEditMode === "edit" && !isEditing}
                                />
                              </div>
                              {/* Remove Button */}
                              {values.tipsForRecovery.length > 1 && (
                                <button
                                  type="button"
                                  className="text-red-500 hover:text-red-700 disabled:opacity-50"
                                  onClick={() => {
                                    if (values.tipsForRecovery.length > 1)
                                      arrayHelpers.remove(index);
                                  }}
                                  disabled={isEditMode === "edit" && !isEditing}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))
                        : arrayHelpers.push("")}

                      {/* Add New Tag Button */}
                      <div className="flex justify-center mt-3">
                        <button
                          type="button"
                          className={`px-4 py-2 bg-white rounded-md border transition ${
                            isEditMode === "edit" && !isEditing
                              ? "text-textGrey border border-dashed"
                              : "text-themeBtn border-themeBtn hover:bg-themeBtn hover:text-white"
                          }`}
                          disabled={isEditMode === "edit" && !isEditing}
                          onClick={() => arrayHelpers.push("")}
                        >
                          + Add Tip For Recovery
                        </button>
                      </div>
                    </div>
                  )}
                />

                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                    Instruction *
                  </label>
                  <div className="relative">
                    <ReactQuill
                      ref={quillRef}
                      value={values.instruction}
                      onChange={(content) =>
                        setFieldValue("instruction", content)
                      }
                      onBlur={() =>
                        handleBlur({ target: { name: "instruction" } })
                      }
                      readOnly={isEditMode === "edit" && !isEditing}
                      modules={modules}
                      formats={formats}
                      theme="snow"
                    />

                    {isUploading && (
                      <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20">
                        <div className="w-12 h-12 border-4 border-themeBtn border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                  <style>{`
                    .ql-editor img {
                      max-width: 200px;
                      max-height: 200px;
                      object-fit: contain;
                    }
                    .ql-editor video {
                      max-width: 400px;
                      max-height: 240px;
                    }
                    .ql-container.ql-snow {
                      border: 1px solid #d1d5db;
                      min-height: 200px;
                    }
                    .ql-editor {
                      min-height: 150px;
                    }
                    ${
                      isEditMode === "edit" && !isEditing
                        ? `
                      .ql-toolbar {
                        display: none !important;
                      }
                      .ql-container.ql-snow {
                        background-color: #f9fafb;
                        border: 1px solid #d1d5db;
                      }
                      .ql-editor {
                        background-color: #f9fafb;
                        color: #6b7280;
                        cursor: not-allowed;
                        pointer-events: none;
                      }
                    `
                        : ""
                    }
                  `}</style>
                  {errors.instruction && touched.instruction && (
                    <InputError errorTitle={errors.instruction} />
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-3">
                <DefaultPrimaryButton
                  type="button"
                  value={isEditMode === "edit" ? "Back" : "Cancel"}
                  onClick={handleCancelClick}
                />
                {isEditMode === "edit" ? (
                  isEditing ? (
                    <>
                      <DefaultButton
                        type="submit"
                        value="Save"
                        onClick={(e) => {}}
                      />
                    </>
                  ) : (
                    <DefaultButton
                      value="Edit"
                      onClick={handleEditClick}
                      type="submit"
                    />
                  )
                ) : (
                  <>
                    <DefaultButton type="submit" value="confirm" />
                  </>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {showConfirmationModal && (
        <ConfirmationModel
          confirmText={isEditMode === "edit" ? "Edit" : "Create"}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleConfirm}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default AddEditTip;
